import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Upload, Loader2, FileDown, Send } from "lucide-react";
import { inr, fdate, INST_COLOR, REQ_LABEL, REQ_COLOR, FeeLedgerDialog } from "@/components/FeeLedgerDialog";
import { FREQ_LABEL } from "@/components/FeePlanDialog";
import { downloadFeeReceipt } from "@/lib/feeExport";

function SubmitDialog({ target, onClose, onDone }) {
  const ref = useRef(null);
  const [amount, setAmount] = useState(0);
  const [txn, setTxn] = useState("");
  const [note, setNote] = useState("");
  const [proof, setProof] = useState("");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setAmount(target.inst.balance + target.inst.late_fee); setTxn(""); setNote(""); setProof(""); setPreview(""); } }, [target]);

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) { toast.error("Choose an image under 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/fee-mgmt/requests/upload-proof", fd);
      setProof(data.proof_path); setPreview(URL.createObjectURL(file));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }
  async function submit() {
    try {
      await api.post("/fee-mgmt/requests", { plan_id: target.plan.id, installment_no: target.inst.no, amount: Number(amount), transaction_id: txn, proof_path: proof, note });
      toast.success("Payment request submitted. The school will verify it."); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-xl">Submit payment proof</DialogTitle></DialogHeader>
        {target && (
          <div className="space-y-3">
            <div className="rounded-xl bg-violet-50 p-3 text-sm">{target.plan.student_name} • {target.inst.label} • due {fdate(target.inst.due_date)}<br />Pending <b className="text-rose-700">{inr(target.inst.balance)}</b>{target.inst.late_fee > 0 && <> + late fee <b>{inr(target.inst.late_fee)}</b></>}</div>
            <div><Label>Amount paid (₹)</Label><Input data-testid="req-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" /></div>
            <div><Label>Transaction ID / UTR</Label><Input data-testid="req-txn" value={txn} onChange={(e) => setTxn(e.target.value)} className="mt-1" placeholder="e.g. 412345678901" /></div>
            <div>
              <Label>Payment screenshot</Label>
              <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="req-proof-input" />
              <div className="flex items-center gap-3 mt-1">
                {preview && <img src={preview} alt="Proof" className="h-16 w-16 rounded-lg object-cover border" />}
                <Button data-testid="req-proof-btn" type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy}>
                  {busy ? <Loader2 size={15} className="animate-spin mr-1" /> : <Upload size={15} className="mr-1" />} {proof ? "Change screenshot" : "Upload screenshot"}
                </Button>
              </div>
            </div>
            <div><Label>Note (optional)</Label><Textarea data-testid="req-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" /></div>
            <p className="text-xs text-slate-500">Your pending balance updates only after the school verifies this payment.</p>
            <Button data-testid="req-submit" onClick={submit} disabled={!proof || txn.length < 3 || Number(amount) <= 0} className="w-full bg-violet-600 hover:bg-violet-700"><Send size={15} className="mr-1" /> Submit request</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ParentFeesPage() {
  const [plans, setPlans] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [target, setTarget] = useState(null);
  const [ledger, setLedger] = useState(null);
  const load = () => {
    api.get("/fee-mgmt/parent/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/fee-mgmt/requests").then((r) => setReqs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const openFor = (pid, no) => reqs.find((q) => q.plan_id === pid && q.installment_no === no && !["payment_verified", "rejected"].includes(q.status));

  return (
    <AppShell>
      <PageHeader eyebrow="Fees" title="My Fees" subtitle="See pending installments, submit payment proof and track verification." />
      {plans.length === 0 ? <Empty testid="parent-fees-empty" icon={Wallet} text="No fee plan has been assigned yet." /> : (
        <div className="space-y-5">
          {plans.map((p) => (
            <div key={p.id} data-testid={`parent-plan-${p.id}`} className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><div className="font-display font-bold text-slate-900 text-lg">{p.student_name}</div><div className="text-xs text-slate-500">{p.class_name} {p.section} • {p.academic_year} • {FREQ_LABEL[p.frequency]}</div></div>
                <Button data-testid={`parent-ledger-${p.id}`} variant="outline" size="sm" onClick={() => setLedger(p.id)}>Full ledger</Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
                <div className="rounded-xl bg-violet-50 p-2.5">Total<div className="font-bold">{inr(p.net_total)}</div></div>
                <div className="rounded-xl bg-emerald-50 p-2.5">Paid<div className="font-bold text-emerald-700">{inr(p.paid)}</div></div>
                <div className="rounded-xl bg-rose-50 p-2.5">Pending<div className="font-bold text-rose-700">{inr(p.pending)}</div></div>
                <div className="rounded-xl bg-amber-50 p-2.5">Late fee<div className="font-bold text-orange-700">{inr(p.late_fee_due)}</div></div>
              </div>
              <div className="mt-3 divide-y divide-slate-100">
                {p.installments.map((i) => {
                  const open = openFor(p.id, i.no);
                  return (
                    <div key={i.no} data-testid={`parent-inst-${p.id}-${i.no}`} className="py-2.5 flex flex-wrap items-center gap-2">
                      <div className="flex-1"><span className="font-semibold text-slate-800">{i.label}</span><span className="text-xs text-slate-500"> • due {fdate(i.due_date)}</span></div>
                      <span className="text-sm">{inr(i.balance)}{i.late_fee > 0 && <span className="text-xs text-orange-700"> +{inr(i.late_fee)}</span>}</span>
                      <Badge className={`${INST_COLOR[i.status]} capitalize`}>{i.status}</Badge>
                      {open ? <Badge className={REQ_COLOR[open.status]}>{REQ_LABEL[open.status]}</Badge>
                        : i.balance > 0 && <Button data-testid={`parent-pay-inst-${p.id}-${i.no}`} size="sm" onClick={() => setTarget({ plan: p, inst: i })} className="bg-violet-600 hover:bg-violet-700">Pay & submit proof</Button>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {reqs.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display font-bold text-slate-900 mb-2">My payment requests</h2>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
            {reqs.map((q) => (
              <div key={q.id} data-testid={`parent-request-${q.id}`} className="px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
                <div className="flex-1"><b>{q.student_name}</b> • Inst {q.installment_no} • {inr(q.amount)} • Txn {q.transaction_id}<div className="text-xs text-slate-500">{fdate(q.created_at)}{q.history?.length > 1 && q.history[q.history.length - 1].note ? ` • ${q.history[q.history.length - 1].note}` : ""}</div></div>
                <Badge className={REQ_COLOR[q.status]}>{REQ_LABEL[q.status]}</Badge>
                {q.payment_id && <Button data-testid={`parent-receipt-${q.id}`} size="sm" variant="outline" onClick={() => downloadFeeReceipt(q.payment_id)}><FileDown size={14} className="mr-1" /> Receipt</Button>}
              </div>
            ))}
          </div>
        </div>
      )}
      <SubmitDialog target={target} onClose={() => setTarget(null)} onDone={load} />
      <FeeLedgerDialog planId={ledger} onClose={() => setLedger(null)} canManage={false} />
    </AppShell>
  );
}
